import { FormHelperText, Grid } from '@mui/material';
import { Fragment, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { FormControlInput, FormControlSelect } from '../../components-molecules/FormControl';
import { FormPageComponent } from '../../components-molecules/FormPageComponent';
import { ModalCardComponent } from '../../components-molecules/Modal';
import { SectionHeader } from '../../components-molecules/section/SectionHeader';
import { EMAIL_VALIDATION, PHONE_VALIDATION_V2, REQUIRED_VALIDATION, validateWhiteSpace } from '../../forms/_predefinedValidations';
import { useTypedSelector } from '../../hooks/TypedReduxHooks';
import { EditAdminUser as EditAdminUserRequest, ListAdminRole, ListAllRoles } from '../../service/serviceService';

interface EditAdminUser {
  firstName: string;
  lastName: string;
  phone: string;
  roleId: GUID;
}

export function EditAdminUser(): JSX.Element {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { state } = useLocation();
  const user = state.selectedUser;
  const { control, handleSubmit, formState: { isValid, errors, isSubmitted }, setError,trigger } = useForm<any>({
    mode: 'all', reValidateMode: 'onChange', defaultValues: {
      email: user.Email,
      firstName: user.FirstName,
      lastName: user.LastName,
      phone: user.Phone,
      roleId: user.RoleId
    }
  });
  const token = useTypedSelector(state => state.userReducer.token);

  const submit: SubmitHandler<EditAdminUser> = (data): void => {
    if (token) {
      EditAdminUserRequest(
        token,
        {
          Id: user.Id,
          FirstName: data.firstName,
          LastName: data.lastName,
          Phone: data.phone,
          RoleId: data.roleId as GUID
        },
        () => {
          setLoading(false);
          navigate('/admin/adminusers');
        },
        (error) => {
          setError('email', { message: error.response?.data as string || '', type: 'value' });
          setLoading(false);
        }
      );
    }
  };

  const [roleList, setRoleList] = useState<ListAdminRole[]>([]);

  useEffect(() => {
    if (token) {
      setLoading(true);
      ListAllRoles(
        token,
        (response) => {
          setRoleList(response.Result);
          setLoading(false);
        },
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {}
      );
    }
    trigger();
  }, [token,trigger]);

  return (
    <FormPageComponent
      buttonLoading={loading}
      title="Edit Admin Portal User"
      buttonTitle="Submit"
      hasError={isSubmitted && ('email' in errors)}
      buttonDisabled={loading || !isValid}
      close={() => navigate('/admin/adminusers')}
      submit={handleSubmit(submit)}
    >
      <Fragment>
        <ModalCardComponent>
          <SectionHeader title="Admin portal user details" />
          <Fragment>
            <Grid item container sx={{ flexGrow: 1 }}>
              <Grid item xs={12}>
                <FormControlInput
                  data-testid="edit-admin-user-firstname"
                  control={control}
                  field={{
                    label: 'First name *',
                    name: 'firstName',
                    inputType: 'text',
                    validation: {
                      required: REQUIRED_VALIDATION,
                      validate: { validateWhiteSpace }
                    }
                  }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlInput
                  data-testid="edit-admin-user-lastname"
                  control={control}
                  field={{
                    label: 'Last Name *',
                    name: 'lastName',
                    inputType: 'text',
                    validation: {
                      required: REQUIRED_VALIDATION,
                      validate: { validateWhiteSpace }
                    }
                  }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlInput
                  disabled
                  data-testid="edit-admin-user-email"
                  control={control}
                  field={{
                    label: 'Email *',
                    name: 'email',
                    inputType: 'text',
                    validation: {
                      required: REQUIRED_VALIDATION,
                      pattern: EMAIL_VALIDATION,
                      validate: { validateWhiteSpace }
                    }
                  }} />
              </Grid>
              <Grid item xs={12}>
                <FormControlInput
                  data-testid="edit-admin-user-phone"
                  control={control}
                  field={{
                    label: 'Phone number',
                    name: 'phone',
                    inputType: 'text',
                    validation: {
                      pattern: PHONE_VALIDATION_V2
                    }
                  }} />
                <FormHelperText sx={{ padding: '0 14px' }}>
                  Example: 07975777666
                </FormHelperText>
              </Grid>
            </Grid>
          </Fragment>
        </ModalCardComponent>
        <ModalCardComponent>
          <SectionHeader
            title="Admin portal user role"
            subtitle="Select a user role you want to assign to this admin portal user."
          />
          <Fragment>
            <Grid item container columnSpacing={4} sx={{ flexGrow: 1 }}>
              <Grid item xs={12}>
                <FormControlSelect
                  data-testid="edit-admin-user-role"
                  control={control}
                  field={{
                    label: 'User role *',
                    name: 'roleId',
                    inputType: 'select',
                    options: roleList.map(m => ({ name: m.Name, value: m.Id })),
                    validation: {
                      required: REQUIRED_VALIDATION
                    }
                  }} />
              </Grid>
            </Grid>
          </Fragment>
        </ModalCardComponent>
      </Fragment>
    </FormPageComponent>
  );
}
